/**
 * Beweging in de hero van de homepage en van de subpagina's.
 *
 * Eén component: het headerblok en het subheaderblok includen allebei
 * partials/header-hero.blade.php, dus wat hier staat geldt voor de homepage én voor elke
 * pagina die met zo'n blok opent.
 *
 * Bij het laden bouwt de hero zich in één doorlopende beweging op: het beeld zakt uit een
 * lichte overmaat terug op zijn plek, de kop komt regel voor regel van onder een masker
 * vandaan, de balk onderin tekent zichzelf, en daaroverheen komen de subtitel, de knoppen,
 * het uitgelichte kaartje en de contactpersoon na elkaar binnen.
 *
 * Tijdens het scrollen loopt de tekst iets trager weg dan de pagina en de achtergrond
 * iets sneller -- dat verschil is de parallax. Bewust klein gehouden; er wordt niets
 * gefade en de tekst blijft de hele tijd leesbaar.
 *
 * Een jaartal in de kop rolt van dit jaar terug naar het jaar dat er staat; de markup komt
 * uit \App\year_odometer() (app/helpers.php). Die odometer bouwt zich op op het moment dat
 * de regels net onder hun masker zijn gezet en dus nog onzichtbaar zijn -- zie
 * bouwOdometer() voor waarom dat precies daar moet.
 *
 * Het splitsen van de koppen staat in hero-motion.js, gedeeld met de andere hero's.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles gewoon
 * op zijn plek. Gaat er onverhoopt iets mis nadat de begintoestand is gezet, dan worden de
 * inline stijlen weer weggehaald: een hero die niet beweegt is een schoonheidsfout, een
 * hero die onzichtbaar blijft is een kapotte pagina.
 *
 * Maten: resources/styles/common/home-header.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import initNumberOdometer from './odometer';
import { EASE, minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

export default function initHomeHeader() {
  const root = document.querySelector('.home-header');

  if (!root) {
    return;
  }

  // Losgetrokken van de rest: de kleur van de hap is geen beweging maar opmaak, en die
  // hoort ook te kloppen als er niets mag bewegen.
  kleurNotch(root);

  if (minderBeweging()) {
    return;
  }

  const heading = root.querySelector('[data-home-header-heading]');
  const notch = root.querySelector('[data-home-header-notch]');
  const bottom = root.querySelector('.home-header__bottom');
  const background = root.querySelector('.home-header__bg');
  // Beeld en video dragen allebei .home-header__media en kunnen samen voorkomen -- de
  // foto ligt onder de video, die van Vimeo komt en laadtijd heeft. Dus allebei schalen.
  const media = Array.from(root.querySelectorAll('.home-header__media'));
  const asideText = root.querySelector('.home-header__aside-text');

  // Alles wat na de kop binnenkomt, in de volgorde waarin je het leest. querySelectorAll
  // met een lijst selectors houdt die volgorde aan de DOM, dus een hero zonder kaartje of
  // zonder contactpersoon levert vanzelf een kortere reeks op.
  const reveals = Array.from(
    root.querySelectorAll(
      '.home-header__subtitle, .home-header__buttons, .home-header__highlight, .home-header__contact'
    )
  );

  const beweegbaar = [...media, notch, ...reveals].filter(Boolean);

  try {
    if (media.length) {
      gsap.set(media, { scale: 1.08 });
    }

    if (notch) {
      gsap.set(notch, { scaleX: 0 });
    }

    gsap.set(reveals, { y: 20, opacity: 0 });

    parallax(root, bottom, background);
    speelIntro(media, notch, reveals);

    // De kop eerst, de tekst rechtsonder er net achteraan: zo lees je het na elkaar in
    // plaats van dat alles tegelijk verschijnt.
    onthulRegels(heading, { klasse: 'home-header__line', naSplit: bouwOdometer });
    onthulRegels(asideText, { klasse: 'home-header__line', delay: 0.45 });
  } catch (fout) {
    gsap.set(beweegbaar, { clearProps: 'all' });

    throw fout;
  }
}

/**
 * Geeft de hap onderin de hero de kleur van wat eronder komt.
 *
 * De hap loopt naadloos over in de sectie eronder -- daar leest hij als een trapje, en
 * niet als een los vlak. Welke sectie dat is verschilt per pagina: soms de introtekst
 * (die staat op de paginakleur), soms een wit blok, soms het blauwe dienstenblok. De
 * kleur vastzetten in de SCSS klopt dus maar in één van die gevallen.
 *
 * Vandaar dat we hem hier uitlezen in plaats van hem af te leiden uit het blok eronder:
 * blokken hebben geen kleurveld, hun achtergrond zit in hun eigen klasse. Wat de browser
 * berekent is de enige plek waar die kleur betrouwbaar staat.
 *
 * Een sectie zonder eigen achtergrond is doorzichtig; dan telt die van de body. Staat er
 * een foto in plaats van een kleur, dan komt de achtergrondkleur eronder eruit -- niet
 * perfect, maar dichterbij dan een vaste kleur.
 *
 * @param {Element} root
 */
function kleurNotch(root) {
  const notch = root.querySelector('[data-home-header-notch]');

  if (!notch) {
    return;
  }

  let volgende = root.nextElementSibling;

  // Een sectie die niets rendert -- een blok zonder inhoud -- bepaalt de kleur niet.
  while (volgende && !volgende.getBoundingClientRect().height) {
    volgende = volgende.nextElementSibling;
  }

  const kleur = volgende && achtergrondkleur(volgende);

  // Op de hero en niet op de hap zelf: custom properties erven door, en de hap krijgt in
  // de foutafhandeling hierboven zijn inline stijlen afgenomen. Daar mag de kleur niet
  // in meegaan.
  if (kleur) {
    root.style.setProperty('--notch-bg', kleur);
  }
}

/**
 * De eerste echte achtergrondkleur op of boven een element.
 *
 * @param {Element} element
 * @return {string|null}
 */
function achtergrondkleur(element) {
  for (let node = element; node; node = node.parentElement) {
    const kleur = getComputedStyle(node).backgroundColor;
    const alpha = kleur.startsWith('rgba') ? parseFloat(kleur.split(',')[3]) : 1;

    if (kleur && alpha > 0) {
      return kleur;
    }
  }

  return null;
}

/**
 * De opbouw bij het laden. Absolute posities op de tijdlijn ("0.45") en geen relatieve:
 * dan verschuift er niets zodra een onderdeel wegvalt bij een hero zonder kaartje.
 */
function speelIntro(media, notch, reveals) {
  const intro = gsap.timeline();

  // Het beeld komt uit een lichte overmaat terug op zijn plek. Traag en over een lange
  // duur: een snelle zoom leest als een effect, deze leest als een camera die tot rust
  // komt. Werkt net zo goed op de videovariant van de homepage.
  if (media.length) {
    intro.to(media, { scale: 1, duration: 2.4, ease: EASE }, 0);
  }

  // De balk onderin tekent zichzelf van niets naar zijn volle breedte -- van de schermrand
  // tot het midden van de container, zie home-header.scss. scaleX in plaats van width, dan
  // hoeft de browser per frame niets opnieuw uit te rekenen; transform-origin staat links
  // in de SCSS. De vertraging laat hem net na de kop starten, zodat het één beweging lijkt.
  if (notch) {
    intro.to(notch, { scaleX: 1, duration: 1.2, ease: EASE }, 0.35);
  }

  // Subtitel, knoppen, kaartje en contactpersoon: kort na elkaar, van onderaf. Klein
  // gehouden -- deze onderdelen moeten binnenkomen, niet opvallen. Ze starten later dan de
  // kop, want die is de reden dat je hier bent.
  if (reveals.length) {
    intro.to(reveals, { y: 0, opacity: 1, duration: 0.9, ease: EASE, stagger: 0.09 }, 0.45);
  }

  return intro;
}

/**
 * Parallax terwijl de hero uit beeld scrollt. De tekst loopt vooruit op de pagina en de
 * achtergrond blijft juist achter; alleen dat verschil zie je. In pixels en niet in
 * procenten: de tekstblokken zijn niet even hoog, dus procenten zouden op mobiel heel
 * anders uitpakken dan op desktop.
 */
function parallax(root, bottom, background) {
  if (!bottom && !background) {
    return;
  }

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: root,
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    },
  });

  if (bottom) {
    timeline.to(bottom, { y: -120, ease: 'none' }, 0);
  }

  if (background) {
    timeline.to(background, { y: 60, ease: 'none' }, 0);
  }
}

/**
 * Bouwt de odometer op in een kop die nog onder zijn masker staat.
 *
 * Het opbouwen vervangt de tekst door kolommen die op het startjaar staan. Gebeurt dat in
 * een zichtbare kop, dan zie je het eindjaar staan en daarna omklappen naar het startjaar
 * -- precies de sprong die je niet wilt. Onder het masker zie je er niets van en komt de
 * kop meteen op het startjaar in beeld.
 *
 * Dit draait bij elke hersplitsing opnieuw, en dat is de bedoeling: SplitText zet bij een
 * nieuw lettertype of een resize de originele HTML terug, en dan hoort de odometer er ook
 * weer opnieuw in. Zijn eigen vlag zou een tweede opbouw tegenhouden, dus die gaat er
 * eerst af.
 *
 * @param {Element} element
 */
function bouwOdometer(element) {
  const groep = element.matches('[data-odometer-group]')
    ? element
    : element.querySelector('[data-odometer-group]');

  if (!groep || !groep.querySelector('[data-odometer-element]')) {
    return;
  }

  groep.removeAttribute('data-odometer-initialized');
  initNumberOdometer();
}
